/**
 * `/mitarbeiter` -- staff, devices, and the owner-only administrators tab.
 *
 * THREE TABS ON ONE ROUTE, AND THE TAB IS IN THE URL. `lib/router.ts`'s
 * `parseStaffTab`/`staffTabHref` own the fragment (Task 6 review m-6: the
 * sub-tab selection never entered router state, so a reload lost it); the
 * tab strip is real anchors with real hrefs, intercepted for a plain left
 * click exactly as Layout's nav is, so a link to a tab is a link and
 * cmd-click still opens a tab.
 *
 * WHICH TABS EXIST IS capabilities.ts'S ANSWER, consumed once, in
 * `visibleStaffTabs`. `resolveStaffTab` then makes the URL harmless: a
 * manager who follows `/mitarbeiter#administratoren` gets the first tab they
 * are allowed, never the owner-only panel -- and never its query either,
 * because the panel is not mounted at all. The server refuses it too
 * (/admin-api/users is owner-guarded); this is the rendering half.
 *
 * DECIDES NOTHING ITSELF, and APPLIES nothing itself either. Every decision
 * (tabs, PIN rules, pills, error copy, the countdown) and every mutation
 * (`*Action`) lives in lib/staffView.ts, where a Node test can hold the
 * collaborators -- the STANDING RULE from the Task 7 and Task 8 reviews,
 * whose escapes were all planted at call sites. What is left here is JSX and
 * component state, and the three call sites that a mutation could still point
 * at the wrong row are asserted in DOM suite #1 (test/dom/layoutGating.test.tsx).
 */
import { useRef, useState } from 'react';
import type { FormEvent, MouseEvent } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../Boot';
import { useRoute } from '../Router';
import { ADMIN_API, AUTH_API } from '../lib/api';
import { guardedAction } from '../lib/gate';
import { t } from '../lib/i18n';
import { isPlainLeftClick, parseStaffTab, staffTabHref } from '../lib/router';
import {
  activeSessions, adminRoleLabel, changeStaffRoleAction, createStaffAction, deviceLastSeenLabel,
  deviceStatusPill, inviteUserAction, isSelf, issuePairingTokenAction, mapDeviceRows,
  resetStaffPinAction, resolveStaffTab, revokeDeviceAction, revokeSessionAction, rowStatusPill,
  sessionLine, staffRoleLabel, staffTabLabel, toggleStaffStatusAction, toggleUserStatusAction,
  visibleStaffTabs, type MutationShell,
} from '../lib/staffView';
import { formatDateTime } from '../lib/startView';
import { devicesQueryOptions, sessionsQueryOptions, staffQueryOptions, usersQueryOptions } from '../queries';
import { ErrorNotice } from '../components/ErrorNotice';
import { PageBody, PageHeader, TabNav } from '../components/ui/page';
import { PairingQr } from '../components/PairingQr';
import { Spinner } from '../components/Spinner';
import { StatusPill } from '../components/StatusPill';
import type { CapabilityCtx } from '../lib/capabilities';
import type { AdminUser, MeResponse, PairingToken, Staff as StaffRow, StaffRole } from '../lib/types';

/** The panel-level busy/error pair every tab shares, plus the client and the
 *  re-entrancy gate.
 *
 *  `gate` is a `useRef` and `busy` is React state, and this view is the reason
 *  the distinction is not academic (final review I-2): `createStaff` run twice
 *  is two staff rows with two PINs, one of which the operator never sees and
 *  cannot read out to anybody. `busy` cannot refuse that second click -- it has
 *  not re-rendered yet. See lib/gate.ts. */
function useMutationShell(): MutationShell & {
  busy: boolean;
  error: string | null;
  gate: { current: string | null };
} {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gate = useRef<string | null>(null);
  return { queryClient, setBusy, setError, busy, error, gate };
}

function PanelError({ message }: { message: string | null }) {
  if (message === null) return null;
  return (
    <p className="staff-error" role="alert">
      {message}
    </p>
  );
}

// ── Mitarbeiter ──────────────────────────────────────────────────────────────

function StaffPanel() {
  const shell = useMutationShell();
  const staff = useQuery(staffQueryOptions());
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const [role, setRole] = useState<StaffRole>('scanner');
  const [pinTarget, setPinTarget] = useState<StaffRow | null>(null);
  const [pinValue, setPinValue] = useState('');
  const [pinShown, setPinShown] = useState<{ name: string; pin: string } | null>(null);

  function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    guardedAction(shell.gate, 'createStaff', () => createStaffAction({ displayName, pin, role }, {
      ...shell,
      createStaff: ADMIN_API.createStaff,
      onCreated: () => {
        setDisplayName('');
        // The PIN leaves the form the moment the write succeeds.
        setPin('');
        setRole('scanner');
      },
    }));
  }

  function openPinDialog(row: StaffRow) {
    setPinTarget(row);
    setPinValue('');
    // A previous reset's PIN must not still be on screen behind the new dialog.
    setPinShown(null);
    shell.setError(null);
  }

  function handlePinReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pinTarget === null) return;
    const target = pinTarget;
    guardedAction(shell.gate, 'updateStaff', () => resetStaffPinAction(target, pinValue, {
      ...shell,
      updateStaff: ADMIN_API.updateStaff,
      onReset: (newPin) => {
        setPinShown({ name: target.displayName, pin: newPin });
        setPinTarget(null);
        setPinValue('');
      },
    }));
  }

  if (staff.isPending) return <Spinner />;
  if (staff.isError) {
    return (
      <div className="staff-panel">
        <ErrorNotice error={staff.error} />
        <button type="button" onClick={() => void staff.refetch()}>{t('blocking.retry')}</button>
      </div>
    );
  }

  return (
    <div className="staff-panel staff-panel-staff">
      <h2>{t('staff.list.title')}</h2>
      <PanelError message={shell.error} />

      {staff.data.staff.length === 0 ? (
        <p>{t('staff.list.empty')}</p>
      ) : (
        <table className="staff-table">
          <thead>
            <tr>
              <th>{t('staff.col.name')}</th>
              <th>{t('staff.col.role')}</th>
              <th>{t('staff.col.status')}</th>
              <th>{t('staff.col.createdAt')}</th>
              <th>{t('staff.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {staff.data.staff.map((row) => {
              const pill = rowStatusPill(row.status);
              return (
                <tr key={row.id}>
                  <td>{row.displayName}</td>
                  <td>
                    {/* aria-label rather than a visible <label>: the column
                        header already names this, and a per-row label would
                        repeat "Rolle" down the whole table. */}
                    <select
                      aria-label={t('staff.col.role')}
                      value={row.role}
                      disabled={shell.busy}
                      onChange={(event) => guardedAction(shell.gate, 'updateStaff', () =>
                        changeStaffRoleAction(
                          row,
                          event.target.value as StaffRole,
                          { ...shell, updateStaff: ADMIN_API.updateStaff },
                        ))}
                    >
                      <option value="scanner">{staffRoleLabel('scanner')}</option>
                      <option value="manager">{staffRoleLabel('manager')}</option>
                    </select>
                  </td>
                  <td><StatusPill kind={pill.kind} label={pill.label} /></td>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>
                    <button
                      type="button"
                      disabled={shell.busy}
                      onClick={() => guardedAction(shell.gate, 'updateStaff', () =>
                        toggleStaffStatusAction(row, { ...shell, updateStaff: ADMIN_API.updateStaff }))}
                    >
                      {row.status === 'active' ? t('staff.action.disable') : t('staff.action.activate')}
                    </button>
                    <button type="button" disabled={shell.busy} onClick={() => openPinDialog(row)}>
                      {t('staff.action.resetPin')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {pinTarget === null ? null : (
        <form
          className="staff-pin-dialog"
          role="dialog"
          aria-label={t('staff.pinDialog.title', { name: pinTarget.displayName })}
          onSubmit={handlePinReset}
        >
          <h3>{t('staff.pinDialog.title', { name: pinTarget.displayName })}</h3>
          <p>{t('staff.pinDialog.hint')}</p>
          <label>
            {t('staff.create.pin')}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              minLength={4}
              maxLength={8}
              required
              value={pinValue}
              disabled={shell.busy}
              onChange={(event) => setPinValue(event.target.value)}
            />
          </label>
          <p className="staff-pin-hint">{t('staff.pin.hint')}</p>
          <button type="submit" disabled={shell.busy}>{t('staff.pinDialog.submit')}</button>
          {/* Review m-5: the typed PIN leaves state on cancel too, not only on
              reopen and on success. It is never rendered and never persisted,
              so this is hygiene rather than a leak -- but a secret that is
              still in memory because a dialog was dismissed is a secret with
              no owner. */}
          <button
            type="button"
            onClick={() => {
              setPinTarget(null);
              setPinValue('');
            }}
          >
            {t('staff.pinDialog.cancel')}
          </button>
        </form>
      )}

      {/* Shown exactly once, and only until this is dismissed: it is not in the
          cache, not in storage and not in any log. */}
      {pinShown === null ? null : (
        <div className="staff-pin-shown" role="status">
          <p>{t('staff.pinDialog.done', { name: pinShown.name, pin: pinShown.pin })}</p>
          <button type="button" onClick={() => setPinShown(null)}>{t('staff.pinDialog.close')}</button>
        </div>
      )}

      <form className="staff-create" onSubmit={handleCreate}>
        <h3>{t('staff.create.title')}</h3>
        <label>
          {t('staff.create.name')}
          <input
            type="text"
            required
            value={displayName}
            disabled={shell.busy}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </label>
        <label>
          {t('staff.create.pin')}
          {/* inputMode numeric + the server's own 4-8 bound. The client check
              in `createStaffAction` MIRRORS the server, it does not replace it
              (adminRoutes.ts's hashPin is the authority). */}
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            minLength={4}
            maxLength={8}
            required
            value={pin}
            disabled={shell.busy}
            onChange={(event) => setPin(event.target.value)}
          />
        </label>
        <p className="staff-pin-hint">{t('staff.pin.hint')}</p>
        <label>
          {t('staff.create.role')}
          {/* scanner | manager only -- `owner` is not a staff role at all
              (adminRoutes.ts's STAFF_ROLES). */}
          <select value={role} disabled={shell.busy} onChange={(event) => setRole(event.target.value as StaffRole)}>
            <option value="scanner">{staffRoleLabel('scanner')}</option>
            <option value="manager">{staffRoleLabel('manager')}</option>
          </select>
        </label>
        <button type="submit" disabled={shell.busy}>{t('staff.create.submit')}</button>
      </form>
    </div>
  );
}

// ── Geräte ───────────────────────────────────────────────────────────────────

function DevicesPanel() {
  const shell = useMutationShell();
  const devices = useQuery(devicesQueryOptions());
  const [label, setLabel] = useState('');
  const [pairing, setPairing] = useState<PairingToken | null>(null);

  function handlePair(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    // Gated by the ref, not by `busy`: this MINTS A CREDENTIAL. A second click
    // would issue a second pairing token and the panel would only ever show
    // one of them -- the other stays live, unshown and unrevokable by anyone
    // who does not know it exists.
    guardedAction(shell.gate, 'pairToken', () => issuePairingTokenAction(label, {
      ...shell,
      pairToken: AUTH_API.pairToken,
      onToken: (token) => {
        setPairing(token);
        setLabel('');
      },
    }));
  }

  if (devices.isPending) return <Spinner />;
  if (devices.isError) {
    return (
      <div className="staff-panel">
        <ErrorNotice error={devices.error} />
        <button type="button" onClick={() => void devices.refetch()}>{t('blocking.retry')}</button>
      </div>
    );
  }

  // api/src/db/devices.ts:84-89 selects raw D1 columns, unlike every
  // /admin-api/* route (plan 3c interface D19, amendment E2). Mapped HERE,
  // once, visibly -- not normalized inside the client, where a later
  // server-side fix would silently blank this table instead of failing a test.
  const rows = mapDeviceRows(devices.data.devices);

  return (
    <div className="staff-panel staff-panel-devices">
      <h2>{t('staff.devices.title')}</h2>
      <PanelError message={shell.error} />

      {rows.length === 0 ? (
        <p>{t('staff.devices.empty')}</p>
      ) : (
        <table className="devices-table">
          <thead>
            <tr>
              <th>{t('staff.devices.col.label')}</th>
              <th>{t('staff.col.status')}</th>
              <th>{t('staff.devices.col.pairedAt')}</th>
              <th>{t('staff.devices.col.lastSeen')}</th>
              <th>{t('staff.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((device) => {
              const pill = deviceStatusPill(device);
              return (
                <tr key={device.id}>
                  <td>{device.label}</td>
                  <td><StatusPill kind={pill.kind} label={pill.label} /></td>
                  <td>{formatDateTime(device.pairedAt)}</td>
                  <td>{deviceLastSeenLabel(device)}</td>
                  <td>
                    {device.revokedAt === null ? (
                      <button
                        type="button"
                        disabled={shell.busy}
                        onClick={() => guardedAction(shell.gate, 'revokeDevice', () =>
                          revokeDeviceAction(device, {
                            ...shell,
                            revokeDevice: AUTH_API.revokeDevice,
                            confirm: (message) => window.confirm(message),
                          }))}
                      >
                        {t('staff.devices.revoke')}
                      </button>
                    ) : (
                      <span className="device-revoked-at">
                        {t('staff.devices.revokedAt', { date: formatDateTime(device.revokedAt) })}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <form className="devices-pair" onSubmit={handlePair}>
        <h3>{t('staff.devices.pairTitle')}</h3>
        <label>
          {t('staff.devices.pairLabel')}
          <input
            type="text"
            required
            maxLength={64}
            value={label}
            disabled={shell.busy}
            onChange={(event) => setLabel(event.target.value)}
          />
        </label>
        <button type="submit" disabled={shell.busy}>{t('staff.devices.pairSubmit')}</button>
      </form>

      {/* State, not cache: this unmounts with the panel on a tab change, which
          is what "shown once, stored nowhere" means in practice. */}
      {pairing === null ? null : (
        <PairingQr token={pairing.token} expiresAt={pairing.expiresAt} onClose={() => setPairing(null)} />
      )}
    </div>
  );
}

// ── Administratoren (owner only) ─────────────────────────────────────────────

function AdminUsersPanel({ me }: { me: MeResponse }) {
  const shell = useMutationShell();
  const users = useQuery(usersQueryOptions());
  const [email, setEmail] = useState('');

  function handleInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    guardedAction(shell.gate, 'inviteUser', () => inviteUserAction(email, {
      ...shell,
      inviteUser: ADMIN_API.inviteUser,
      onInvited: () => setEmail(''),
    }));
  }

  return (
    <div className="staff-panel staff-panel-users">
      <h2>{t('staff.users.title')}</h2>
      <PanelError message={shell.error} />

      {users.isPending ? <Spinner /> : users.isError ? (
        <>
          <ErrorNotice error={users.error} />
          <button type="button" onClick={() => void users.refetch()}>{t('blocking.retry')}</button>
        </>
      ) : users.data.users.length === 0 ? (
        <p>{t('staff.users.empty')}</p>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th>{t('staff.users.col.email')}</th>
              <th>{t('staff.users.col.role')}</th>
              <th>{t('staff.col.status')}</th>
              <th>{t('staff.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.data.users.map((user: AdminUser) => {
              const pill = rowStatusPill(user.status);
              const self = isSelf(user, me.user.id);
              return (
                <tr key={user.id}>
                  <td>{user.email}</td>
                  <td>{adminRoleLabel(user.role)}</td>
                  <td><StatusPill kind={pill.kind} label={pill.label} /></td>
                  <td>
                    {/* `cannot_disable_self` rendered BEFORE the click: a
                        request whose only answer is a 400 is not a control. */}
                    <button
                      type="button"
                      disabled={shell.busy || self}
                      title={self ? t('staff.users.cannotDisableSelf') : undefined}
                      onClick={() => guardedAction(shell.gate, 'updateUser', () =>
                        toggleUserStatusAction(user, me.user.id, {
                          ...shell,
                          updateUser: ADMIN_API.updateUser,
                        }))}
                    >
                      {user.status === 'active' ? t('staff.action.disable') : t('staff.action.activate')}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <form className="users-invite" onSubmit={handleInvite}>
        <h3>{t('staff.users.invite.title')}</h3>
        {/* No role selector, by design: `inviteUserAction` sends `manager` as a
            constant. Owner carries billing and deletion, and handing that out
            from a self-serve form has no undo (adminRoutes.ts:437-439). */}
        <p className="users-invite-role-note">{t('staff.users.invite.roleNote')}</p>
        <label>
          {t('staff.users.invite.email')}
          <input
            type="email"
            required
            autoComplete="off"
            value={email}
            disabled={shell.busy}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="submit" disabled={shell.busy}>{t('staff.users.invite.submit')}</button>
      </form>

      <SessionsSection />
    </div>
  );
}

function SessionsSection() {
  const shell = useMutationShell();
  const sessions = useQuery(sessionsQueryOptions());

  if (sessions.isPending) return <Spinner />;
  if (sessions.isError) {
    return (
      <section className="staff-sessions">
        <h3>{t('staff.sessions.title')}</h3>
        <ErrorNotice error={sessions.error} />
        <button type="button" onClick={() => void sessions.refetch()}>{t('blocking.retry')}</button>
      </section>
    );
  }

  const rows = activeSessions(sessions.data.sessions, Date.now());

  return (
    <section className="staff-sessions">
      {/* "Ihre Sitzungen", never "alle Sitzungen": 2b's revoke is
          own-sessions-only (api/src/authRoutes.ts). */}
      <h3>{t('staff.sessions.title')}</h3>
      <p>{t('staff.sessions.note')}</p>
      <PanelError message={shell.error} />
      {rows.length === 0 ? (
        <p>{t('staff.sessions.empty')}</p>
      ) : (
        <table className="sessions-table">
          <thead>
            <tr>
              <th>{t('staff.sessions.col.createdAt')}</th>
              <th>{t('staff.sessions.col.expiresAt')}</th>
              <th>{t('staff.col.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((session) => {
              const line = sessionLine(session);
              return (
                <tr key={session.id}>
                  <td>{line.createdAt}</td>
                  <td>{line.expiresAt}</td>
                  <td>
                    {session.current ? (
                      <span className="session-current">{t('staff.sessions.current')}</span>
                    ) : (
                      <button
                        type="button"
                        disabled={shell.busy}
                        onClick={() => guardedAction(shell.gate, 'revokeSession', () =>
                          revokeSessionAction(session, {
                            ...shell,
                            revokeSession: AUTH_API.revokeSession,
                            confirm: (message) => window.confirm(message),
                          }))}
                      >
                        {t('staff.sessions.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ── the view ─────────────────────────────────────────────────────────────────

export function Staff() {
  const me = useMe();
  const { hash, navigate } = useRoute();

  // Boot resolves `me` before any view mounts, so this is a cache read. The
  // guard is what lets the type narrow without a cast -- the same shape Boot's
  // own `me.isSuccess` guard uses.
  if (!me.isSuccess) return <Spinner />;

  const ctx: CapabilityCtx = { role: me.data.role, status: me.data.tenant.status, features: me.data.features };
  const visible = visibleStaffTabs(ctx);
  const tab = resolveStaffTab(parseStaffTab(hash), visible);

  function handleTabClick(event: MouseEvent<HTMLAnchorElement>, href: string) {
    if (!isPlainLeftClick(event)) return;
    event.preventDefault();
    navigate(href);
  }

  return (
    <section className="staff-view">
      <PageBody>
        <PageHeader title={t('nav.staff')} />
        {/* Which tabs EXIST is `visibleStaffTabs`'s answer, computed above and
            passed in as a list. <TabNav> renders what it is given and reads no
            capability and no URL of its own. */}
        <TabNav
          className="staff-tabs"
          label={t('staff.tabs.label')}
          activeId={tab}
          items={visible.map((id) => ({ id, label: staffTabLabel(id), href: staffTabHref(id) }))}
          onTabClick={(event, href) => handleTabClick(event, href)}
        />

        {tab === 'staff' ? <StaffPanel /> : null}
        {tab === 'devices' ? <DevicesPanel /> : null}
        {tab === 'adminUsers' ? <AdminUsersPanel me={me.data} /> : null}
      </PageBody>
    </section>
  );
}
